// ink-component
import React, { InputHTMLAttributes, ReactNode, ChangeEvent, RefObject } from 'react';
import { ThemeConsumer } from 'styled-components';
import { NumberFormatProps } from 'react-number-format';

import CoreInput from '../_common/CoreInput/CoreInput';

import TableInput from './components/TableInput/TableInput';
import { ControllerProps } from '../_common/Controller/Controller';
import getGlobalProps from '../../helpers/constants/global-props';

export type CustomNumberFormatProps = Pick<
  NumberFormatProps,
  | 'decimalSeparator'
  | 'thousandSeparator'
  | 'decimalScale'
  | 'allowNegative'
  | 'isNumericString'
  | 'fixedDecimalScale'
  | 'isAllowed'
>;

export type MaskType =
  | 'ssn'
  | 'ein'
  | 'zipcode'
  | 'phone'
  | 'date'
  | 'short-date'
  | 'integer'
  | 'decimal'
  | string
  | CustomNumberFormatProps;

interface CustomReturnValue {
  unmaskedValue: string;
}

export type NewInputProps = Omit<ControllerProps, 'render' | 'theme'> &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'prefix' | 'inputMode'> & {
    /** onChange callback, sends the event */
    onChange?: (event: ChangeEvent<HTMLInputElement>, value: CustomReturnValue) => void;
    /** Width of the input */
    width?: 'large' | 'half' | 'quarter' | 'full';
    /** Modifier for inline */
    inline?: boolean;
    /** Modifier for invalid state */
    invalid?: boolean;
    /** Modifier for disabled state */
    disabled?: boolean;
    /** Defines a mask. Can be a random pattern string (e.g: `##/##/####` for phone numbers),
     * a predefined mask (ssn, ein, zipcode, phone, date, short-date, integer, decimal),
     *  or a custom object (e.g: `{ allowNegative: false }`)  */
    mask?: MaskType;
    /** Maximum value for `integer` and `decimal` masks */
    maxValue?: number;
    /** Alignment of the inner text */
    align?: 'left' | 'right';
    /** Prefix content */
    prefix?: ReactNode;
    /** Suffix content */
    suffix?: ReactNode;
    /** Value of the input */
    value?: InputHTMLAttributes<HTMLInputElement>['value'];
    /** HTML type modifier */
    inputType?: 'text' | 'password' | 'number' | 'email' | 'tel' | 'search';
    /** HTML inputMode modifier. Used to specify keyboards on mobile. For example, use 'numeric' for a numeric keyboard. */
    inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
    /** React ref object to be forwarded to the internal node */
    externalRef?: RefObject<HTMLInputElement>;
    /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
    'aria-label'?: string;
    /** Marks the element with a given ID to make it easier to select in tests */
    'data-testid'?: string | number;
    /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
    'data-track-name'?: string;
    /** Custom payload to be sent to analytics provider */
    'data-track-payload'?: any;
    /** List of tracked events in the analytics integration */
    'data-track-events'?: string[];
    /** @deprecated:externalRef This prop is deprecated and will be removed. */
    inputRef?: RefObject<any>;
  };

/**
 * @purpose Allows users to provide one piece of data
 * @categories form
 * @aka input
 * @HTMLType HTMLInputElement
 */
function NewInput(props: NewInputProps) {
  const { className, style, ...otherProps } = props;

  let globalProps = {};

  if (otherProps.inputRef) {
    globalProps = getGlobalProps({ deprecatedProps: true });
  }

  return (
    <ThemeConsumer>
      {cellTheme => {
        if (cellTheme && cellTheme.cellPreset === 'editable') {
          return <TableInput {...otherProps} {...globalProps} />;
        }
        return <CoreInput {...otherProps} {...globalProps} />;
      }}
    </ThemeConsumer>
  );
}

NewInput.defaultProps = {
  value: undefined,
  onChange: undefined,
  width: 'large',
  align: 'left',
  prefix: undefined,
  suffix: undefined,
  invalid: false,
  disabled: false,
  inline: false,
  mask: undefined,
  inputType: 'text',
  step: 1,
  'data-track-events': ['blur'],
};

NewInput.displayName = 'NewInput';

export default NewInput;
